import { useEffect, useState } from "react";
import moment from "moment";

import ReportService from "src/Services/API/report.service";
import Loader from "src/Components/Common/Loader";
import { can, handleError } from "src/Common/function";

function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-[38px] h-[22px] rounded-full transition-colors duration-150 ${
        checked ? "bg-[#0065FD]" : "bg-[#CBD5E1]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-150 ${
          checked ? "translate-x-[16px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/**
 * The weekly sign-off switch, per branch.
 *
 * Off is how the system worked before the sign-off existed: technicians log
 * hours, nothing is submitted, nothing locks. On asks every technician whose
 * home branch this is to submit each week from the app, and locks a week
 * once it is signed. Weeks that ended before the day it was turned on are
 * never asked for, so flipping it on does not paint the past as outstanding.
 *
 * Unlike the lunch switches this does not save on click. A flip changes what
 * is demanded of a whole branch and what is locked, so it asks for a reason
 * first, and the reason is written to each technician's hours history.
 */
const SignoffRulesDrawer = ({ isOpen, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null); // { branchId, enabled, reason }
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const editable = can("time-management:setSignoff");

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return undefined;
    }
    let secondId;
    const firstId = requestAnimationFrame(() => {
      secondId = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(firstId);
      if (secondId) cancelAnimationFrame(secondId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setDraft(null);
    setNotice(null);
    ReportService.getSignoffRules()
      .then((response) => setRules(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const confirm = () => {
    if (!draft?.reason.trim()) return;
    setSaving(true);
    ReportService.setSignoffRule(draft.branchId, draft.enabled, draft.reason.trim())
      .then((response) => {
        const row = response?.data;
        if (row && typeof row === "object") {
          setRules((prev) => prev.map((item) => (item.branchId === row.branchId ? { ...item, ...row } : item)));
          setNotice(
            `${row.branchName}: weekly sign-off is now ${row.enabled ? "on" : "off"}. ${
              row.notified ?? 0
            } technician${row.notified === 1 ? "" : "s"} notified.`
          );
        }
        setDraft(null);
      })
      .catch((error) => handleError(error))
      .finally(() => setSaving(false));
  };

  if (!isOpen && !visible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[520px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[18px] leading-6 font-OpenSansSemiBold text-[#1A2332]">Weekly Sign-off</p>
            <p className="mt-1 text-[13px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              Per branch, whether technicians submit a weekly time card from the app. Off is the way the
              system worked before: hours are logged, nothing is submitted, nothing locks. A technician
              follows their home branch.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#FBFCFF] border-t border-[#E5E8F3] px-4 py-3">
          {notice ? (
            <div className="mb-2 rounded-[8px] border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-[12px] leading-[16px] font-OpenSansRegular text-[#166534]">
              {notice}
            </div>
          ) : null}
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader variant="inline" ariaLabel="loading-signoff-rules" />
            </div>
          ) : rules.length === 0 ? (
            <p className="py-10 text-center text-[14px] font-OpenSansRegular text-[#868DA6]">No branches found.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {rules.map((rule) => {
                const editing = draft?.branchId === rule.branchId;
                return (
                  <div key={rule.branchId} className="bg-white rounded-[8px] border border-[#E5E8F3] px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                          {rule.branchName || "Unnamed branch"}
                        </p>
                        <p className="text-[12px] leading-[16px] font-OpenSansRegular text-[#868DA6]">
                          {rule.enabled
                            ? `On since ${moment(rule.since).format("MMM D, YYYY")}. Weeks ending from that day are submitted and lock once signed.`
                            : rule.since
                              ? `Off. Was on from ${moment(rule.since).format("MMM D, YYYY")}; weeks signed then stay on record.`
                              : "Off. Hours are logged the old way; nothing is submitted."}
                        </p>
                      </div>
                      <Switch
                        label={`Weekly sign-off at ${rule.branchName}`}
                        checked={editing ? draft.enabled : rule.enabled === true}
                        disabled={!editable || saving || (draft && !editing)}
                        onChange={(value) =>
                          editing && value === rule.enabled
                            ? setDraft(null)
                            : setDraft({ branchId: rule.branchId, enabled: value, reason: "" })
                        }
                      />
                    </div>

                    {editing ? (
                      <div className="mt-3 rounded-[6px] border border-[#F5C76A] bg-[#FFF7E6] px-3 py-2.5">
                        <p className="text-[12px] leading-[16px] font-OpenSansSemiBold text-[#92400E]">
                          {draft.enabled ? `Turn weekly sign-off on for ${rule.branchName}?` : `Turn weekly sign-off off for ${rule.branchName}?`}
                        </p>
                        <p className="mt-1 text-[12px] leading-[16px] font-OpenSansRegular text-[#92400E]">
                          {draft.enabled
                            ? "From today, every technician whose home branch this is submits their week from the app, and a submitted week locks until the office reopens it. Weeks that ended before today are not asked for. Each technician is notified."
                            : "Technicians here go back to logging hours as before. Nothing locks and nothing needs submitting. Weeks already signed stay on record. Each technician is notified."}
                        </p>
                        <textarea
                          value={draft.reason}
                          onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
                          placeholder="Why? Written to each technician's hours history."
                          rows={2}
                          className="mt-2 w-full rounded-[6px] border border-[#E5E8F3] bg-white px-2.5 py-1.5 text-[13px] font-OpenSansRegular text-[#1A2332] outline-none focus:border-[#0065FD]"
                        />
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDraft(null)}
                            disabled={saving}
                            className="h-8 px-3 rounded-lg border border-[#E2E8F0] bg-white text-[#1F2A44] text-[13px] font-OpenSansSemiBold"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={confirm}
                            disabled={saving || !draft.reason.trim()}
                            className={`h-8 px-3 rounded-lg text-white text-[13px] font-OpenSansSemiBold ${
                              saving || !draft.reason.trim() ? "bg-[#CAD5E2]" : "bg-[#0065FD] hover:bg-[#0052CC]"
                            }`}
                          >
                            {saving ? "Saving…" : draft.enabled ? "Turn on" : "Turn off"}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E8F3] flex items-center justify-between gap-3">
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
            {editable ? "Each change asks for a reason before it is applied." : "You can view these settings but not change them."}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-[#E2E8F0] text-[#1F2A44] text-[14px] font-OpenSansSemiBold"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

export default SignoffRulesDrawer;
